// SearchDrawer.jsx
import { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Stack,
  Card,
  Divider,
  useTheme,
  OutlinedInput,
  InputAdornment,
  Button,
  Chip,
  CardMedia,
  CardContent,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import { pageStyles } from "../../Styles/pageStyles";
import { commonServices } from "../../Services/commonServices";
import { MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { textFormatter, currencyFormatter, widthData, isOpenNow } from "@shared/Functions/apiHandler";
import StationDetailDrawer from "../pickUp/StationDetailDrawer";
import NavigationIcon from "@mui/icons-material/Navigation";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

export default function SearchDrawer({ open, onCloseModel, location }) {
  const theme = useTheme();
  const classes = pageStyles(theme);
  const token = sessionStorage.getItem("token");
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState(null);
  const [stationOpen, setStationOpen] = useState(false);
  const [serverTimeUtc, setServerTimeUtc] = useState(null);

  const navigate = useNavigate();
  const mtoKm = (distanceMeters) => {
    return (distanceMeters / 1000).toFixed(2);
  };

  const getSearchProducts = async () => {
    if(!location?.lat && !location?.lng) return;
    if (!token) return;
    const payload = {
      term: search?.trim() || "",
      lat: location?.lat || "16.9478",
      lng: location?.lng || "82.2420"
    };
    const res = await commonServices.getSearchProductsAndVendors(payload);
    setServerTimeUtc(res.data?.data?.serverTimeUtc);
    setProducts(res.data?.data?.deliveryProducts || []);
    setVendors(res.data?.data?.vendors || []);
  };

  useEffect(() => {
    getSearchProducts();
  }, [search, open]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        onCloseModel();
      }}
      ModalProps={{ keepMounted: true }}
      slotProps={{
        paper: {
          sx: widthData,
        },
      }}
    >

      <StationDetailDrawer
        open={stationOpen}
        onClose={() => setStationOpen(false)}
        vendorId={vendorId}
      />


      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={classes.headerRow} mb={1}>
          <IconButton onClick={onCloseModel} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">Search</Typography>
        </Box>

        <Divider />

        {/* Search input */}
        <Box my={2}>
          <OutlinedInput
            fullWidth
            size="small"
            placeholder="Search products or vendors..."
            value={search}
            onChange={handleSearch}
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            }
            sx={classes.inputField}
          />
        </Box>

        <Divider />

        {/* products list */}
        <Box sx={{ mt: 2, overflowY: "auto", maxHeight: "90vh", pr: 1, mb: 1 }}>
          <Stack spacing={1}>
            {products.map((fuel, index) => (
              <Card
                key={fuel.productId}
                sx={{ ...classes.txnCard, ...classes.flex_c_c }}
                onClick={() => {
                  onCloseModel();
                  navigate("/main/delivery", { state: fuel });
                }}
              >
                <CardMedia
                  component="img"
                  key={index}
                  image={fuel.imageUrl}
                  alt={fuel.name}
                  sx={classes.productImg}
                />

                <CardContent sx={{ flex: 1, p: 0 }}>
                  <Typography sx={classes.darkTextsmd500}>
                    {fuel.name}
                  </Typography>
                  <Typography sx={classes.lightTextSmall}>
                    {textFormatter(fuel.subCategoryName, 60)}
                  </Typography>
                  <Typography sx={classes.darkTextsmd500}>
                    {currencyFormatter()}
                    {fuel.price}/{fuel.measurementName}
                  </Typography>

                </CardContent>

                <Chip
                  size="small"
                  variant="outlined"
                  label={fuel.stock > 0 ? "Available" : "Unavailable"}
                  color={fuel.stock > 0 ? "success" : "error"}
                />
              </Card>
            ))}
          </Stack>
        </Box>

        {/* vendors list */}
        <Box>
          <Stack spacing={1}>
            {vendors.map((t, index) => (
              <Card key={index} sx={classes.txnCard1} elevation={0}
                onClick={() => {
                  onCloseModel();
                  setVendorId(t.vendorId);
                  setStationOpen(true);
                }}
              >
                <Box sx={classes.flex_c_c}>
                  <CardMedia
                    component="img"
                    key={index}
                    image={t.imageUrl}
                    alt={t.name}
                    sx={classes.productImg}
                  />

                  <Box sx={classes.flex_c_c}>

                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>{t.name}</Typography>
                      <Typography sx={classes.lightTextSmall}>
                        {textFormatter(t.address, 60)}
                      </Typography>
                      <Box
                        sx={classes.flex_c}
                        my={0.5}
                      >
                        <MapPin size={16} />
                        <Typography sx={classes.lightTextSmall}>
                          {mtoKm(t.distanceMeters)} km away
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ textAlign: "right", px: 1}}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={isOpenNow(t.openTime, t.closeTime, serverTimeUtc) ? "Open" : "Closed"}
                        color={isOpenNow(t.openTime, t.closeTime, serverTimeUtc) ? "success" : "error"}
                        sx={isOpenNow(t.openTime, t.closeTime, serverTimeUtc) ? classes.greenChip : undefined}
                        icon={<AccessTimeIcon />}
                      />  

                      <Button
                        startIcon={
                          <NavigationIcon sx={{ transform: "rotate(45deg)" }} />
                        }
                        size="small"
                        sx={{ ...classes.navigateBtn, mt: 1 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(
                            `https://www.google.com/maps/search/?api=1&query=${t.coordinates?.[0]},${t.coordinates?.[1]}`,
                            "_blank"
                          );
                        }}
                      >
                        Navigate
                      </Button>
                    </Box>
                  </Box>

                </Box>

                {/* Fuel chips */}
                <Box sx={{ mt: 1 }}>
                  {t?.products?.map((f, i) => (
                    <Button
                      key={i}
                      size="small"
                      variant="outlined"
                      sx={f.stock > 0 ? classes.priceBtn2 : classes.priceBtn}
                    >
                      <Typography
                        sx={{ ...classes.darkTextSmall500, mr: 1 }}
                      >
                        {f.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="#1C0E0090"
                        fontSize={12}
                      >
                        {currencyFormatter()} {f.price} / {f.priceUnit}{" "}
                        {f.measurementName}
                      </Typography>
                    </Button>
                  ))}
                </Box>
              </Card>
            ))}
          </Stack>
        </Box>

        {(products.length === 0 && vendors.length === 0) && (
          <Typography color="text.secondary">
            No Products/Vendors Found for "{search}".
          </Typography>
        )}
      </Box>
    </Drawer>
  );
}
